// App-side delegate for `@heidi/commerce/data` (D2 batch 3, ARCFE-976). The
// mutation moved; this file keeps the name, the options shape, and every
// user-visible effect in its original order.
import { useBillingErrorToast } from '@/hooks/mutation/billing/useBillingErrorToast';
import { type ErrorResponse } from '@/types';
import { useMutateApplySubscriptionDiscount as useMutateApplySubscriptionDiscountCore } from '@heidi/commerce/data';
import { toast } from '@heidi/design-system/toast';
import { AxiosError } from 'axios';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { invalidateCurrentUser } from '../query/useQueryUser';
import { invalidateCurrentUserBillingInfo } from '../query/useQueryUserBillingInfo';

export { type ApplySubscriptionDiscountRequest } from '@heidi/commerce/contracts';

type UseMutateApplySubscriptionDiscountOptions = {
  /** Runs after a successful apply (e.g. close a modal). Kept fresh via ref. */
  onApplied?: () => void;
};

export function useMutateApplySubscriptionDiscount(
  options: UseMutateApplySubscriptionDiscountOptions = {}
) {
  const intl = useIntl();
  const showBillingError = useBillingErrorToast();
  const onAppliedRef = useRef(options.onApplied);
  useEffect(() => {
    onAppliedRef.current = options.onApplied;
  }, [options.onApplied]);

  return useMutateApplySubscriptionDiscountCore({
    onDiscountApplied: () => {
      void invalidateCurrentUser();
      void invalidateCurrentUserBillingInfo();
      toast({
        tone: 'success',
        title: intl.formatMessage({
          defaultMessage: 'Your discount has been applied to your subscription.',
          id: 'oC4JP+R5jP',
        }),
      });
      onAppliedRef.current?.();
    },
    onDiscountError: (error) => {
      const title = intl.formatMessage({
        defaultMessage: 'Could not apply discount',
        id: 'oNETWbcGgO',
      });
      const backendDescription =
        error instanceof AxiosError
          ? (error.response?.data as ErrorResponse | undefined)?.detail?.msg
          : undefined;
      showBillingError({ title, description: backendDescription });
    },
  });
}
